<template>
  <b-overlay
    :show="show"
    variant="transparent"
    opacity="0.99"
    blur="5px"
    rounded="sm"
  >
    <template #overlay>
      <div class="text-center">
        <b-icon-controller
          font-scale="3"
          animation="cylon"
        />
        <p id="cancel-label">
          กรุณารอสักครู่...
        </p>
      </div>
    </template>

    <!-- Search -->
    <div class="card">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-center">
          <h2 class="text-dark font-medium-3 mb-0">
            <i class="fa fa-search" /> ค้นหารายการฝาก Ezrich
          </h2>
          <span
            class="badge"
            :class="sseConnected ? 'badge-light-success' : 'badge-light-secondary'"
          >
            <i class="fa fa-circle mr-1" :style="{ color: sseConnected ? '#28c76f' : '#b8c2cc', fontSize: '8px' }" />
            {{ sseConnected ? 'Live Update' : 'Connecting...' }}
          </span>
        </div>
        <div class="row">
          <div class="col-md-3">
            <div class="form-group">
              <label>Keyword</label>
              <input
                v-model="search_val"
                type="text"
                class="form-control"
                placeholder="ค้นหา username, เบอร์, orderId, ชื่อ"
                @keyup.enter="getOrders()"
              >
            </div>
          </div>
          <div class="col-md-3">
            <div class="form-group">
              <label>สถานะ</label>
              <b-form-select
                v-model="search_type"
                class="d-inline-block mr-1"
                :options="search_type_option"
              />
            </div>
          </div>
          <div class="col-md-3">
            <div class="form-group">
              <label>วันที่เริ่มต้น</label>
              <flat-pickr
                v-model="dateStart"
                class="form-control"
                placeholder="เลือกเวลาเริ่มต้น"
                :config="{ enableTime: true, dateFormat: 'Y-m-d H:i', time_24hr: true }"
              />
            </div>
          </div>
          <div class="col-md-3">
            <div class="form-group">
              <label>วันที่สิ้นสุด</label>
              <flat-pickr
                v-model="dateEnd"
                class="form-control"
                placeholder="เลือกเวลาสิ้นสุด"
                :config="{ enableTime: true, dateFormat: 'Y-m-d H:i', time_24hr: true }"
              />
            </div>
          </div>
          <div class="col-md-3 mt-2">
            <button
              class="btn btn-gradient-dark"
              @click="getOrders()"
            >
              <feather-icon icon="SearchIcon" />
              ค้นหา
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Table -->
    <b-card no-body>
      <b-table
        striped
        hover
        responsive
        class="position-relative items-center"
        :per-page="perPage"
        :current-page="currentPage"
        :items="items"
        :fields="fields"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :sort-direction="sortDirection"
      >
        <template #cell(index)="data">
          {{ perPage * (currentPage - 1) + (data.index + 1) }}
        </template>

        <template #cell(amount)="data">
          <span class="font-weight-bold text-primary">
            {{ Number(data.item.amount).toLocaleString() }}
          </span>
        </template>

        <template #cell(status)="data">
          <b-badge
            :variant="statusVariant(data.item.status)"
            pill
          >
            {{ statusText(data.item.status) }}
          </b-badge>
        </template>

        <template #cell(dest_account)="data">
          <template v-if="getDetail(data.item)">
            <small>{{ getDetail(data.item).account_name }}</small><br>
            <small class="text-muted">{{ getDetail(data.item).account_number }}</small>
          </template>
          <span v-else class="text-muted">-</span>
        </template>

        <template #cell(created_at)="data">
          {{ formatDate(data.item.created_at) }}
        </template>

        <template #cell(actions)="data">
          <div class="text-nowrap">
            <b-button
              v-if="data.item.status === 'waiting'"
              v-b-modal.modal-upload-slip
              variant="outline-warning"
              size="sm"
              @click="selectOrder(data.item)"
            >
              <feather-icon icon="UploadIcon" size="14" />
              แนบสลิป
            </b-button>
            <b-badge v-else-if="data.item.status === 'success'" variant="light-success">
              <feather-icon icon="CheckCircleIcon" size="14" />
              สำเร็จ
            </b-badge>
            <b-badge v-else variant="light-secondary">
              -
            </b-badge>
          </div>
        </template>
      </b-table>

      <b-card-body class="d-flex justify-content-between flex-wrap pt-0">
        <b-form-group
          label="Per Page"
          label-cols="8"
          label-align="left"
          label-size="sm"
          label-for="perPageSelect"
          class="text-nowrap mb-md-0 mr-1"
        >
          <b-form-select
            id="perPageSelect"
            v-model="perPage"
            size="sm"
            inline
            :options="pageOptions"
          />
        </b-form-group>

        <div>
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            first-number
            last-number
            prev-class="prev-item"
            next-class="next-item"
            class="mb-0"
          >
            <template #prev-text>
              <feather-icon icon="ChevronLeftIcon" size="18" />
            </template>
            <template #next-text>
              <feather-icon icon="ChevronRightIcon" size="18" />
            </template>
          </b-pagination>
        </div>
      </b-card-body>

      <!-- Upload Slip Modal -->
      <b-modal
        id="modal-upload-slip"
        title="แนบสลิปยืนยันการโอนเงิน"
        size="lg"
        ok-title="อัพโหลดสลิป"
        cancel-title="ยกเลิก"
        :ok-disabled="!slipFile || uploading"
        @ok.prevent="submitSlip"
        @hidden="resetModal"
      >
        <div v-if="selectedOrder">
          <!-- Order Summary -->
          <div class="row mb-3">
            <div class="col-md-6">
              <table class="table table-sm table-borderless">
                <tr>
                  <td class="text-muted" style="width:120px">Username</td>
                  <td class="font-weight-bold">{{ selectedOrder.username }}</td>
                </tr>
                <tr>
                  <td class="text-muted">ชื่อ-นามสกุล</td>
                  <td>{{ selectedOrder.fullname }}</td>
                </tr>
                <tr>
                  <td class="text-muted">เบอร์โทร</td>
                  <td>{{ selectedOrder.tel }}</td>
                </tr>
                <tr>
                  <td class="text-muted">Order ID</td>
                  <td><code>{{ selectedOrder.orderId }}</code></td>
                </tr>
                <tr>
                  <td class="text-muted">จำนวนเงิน</td>
                  <td class="font-weight-bold text-primary">{{ Number(selectedOrder.amount).toLocaleString() }} บาท</td>
                </tr>
              </table>
            </div>
            <div class="col-md-6">
              <div v-if="getDetail(selectedOrder)" class="mb-2">
                <small class="text-muted">บัญชีปลายทาง</small><br>
                <span>{{ getDetail(selectedOrder).account_name }}</span><br>
                <span class="text-muted">{{ getDetail(selectedOrder).account_number }}</span>
              </div>
              <div v-if="getDetail(selectedOrder) && getDetail(selectedOrder).image_url">
                <small class="text-muted">QR / รูปภาพ</small><br>
                <img
                  :src="getDetail(selectedOrder).image_url"
                  alt="payment-image"
                  class="img-fluid rounded border mt-1"
                  style="max-height: 200px"
                >
              </div>
            </div>
          </div>

          <hr>

          <!-- File Input -->
          <div class="mb-2">
            <label class="font-weight-bold">เลือกรูปสลิป</label>
            <input
              ref="slipInput"
              type="file"
              class="form-control"
              accept="image/jpeg,image/png,image/webp"
              @change="onFileSelected"
            >
            <small class="text-muted">รองรับ JPG, PNG, WEBP ขนาดไม่เกิน 5MB</small>
          </div>

          <!-- Preview -->
          <div v-if="slipPreview" class="position-relative d-inline-block mb-2">
            <img
              :src="slipPreview"
              alt="slip-preview"
              class="img-fluid rounded border"
              style="max-height: 250px"
            >
            <b-button
              variant="danger"
              size="sm"
              class="position-absolute"
              style="top: 4px; right: 4px"
              @click="clearSlip"
            >
              <feather-icon icon="XIcon" size="14" />
            </b-button>
          </div>

          <!-- Upload Progress -->
          <div v-if="uploading" class="text-center py-2">
            <b-spinner variant="primary" small />
            <span class="ml-1">กำลังอัพโหลด...</span>
          </div>
        </div>
      </b-modal>
    </b-card>
  </b-overlay>
</template>

<script>
import moment from 'moment-timezone'
import {
  BBadge, BModal, BCard, BTable, BFormGroup, BFormSelect, BPagination,
  BFormInput, BButton, BCardBody, BOverlay, BIconController, BSpinner,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import flatPickr from 'vue-flatpickr-component'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    flatPickr,
    BBadge,
    BModal,
    BCard,
    BTable,
    BFormGroup,
    BFormSelect,
    BPagination,
    BFormInput,
    BButton,
    BCardBody,
    BOverlay,
    BIconController,
    BSpinner,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      show: true,
      search_val: null,
      search_type: null,
      search_type_option: [
        { value: null, text: 'ทั้งหมด' },
        { value: 'waiting', text: 'รอดำเนินการ' },
        { value: 'success', text: 'สำเร็จ' },
        { value: 'cancel', text: 'ยกเลิก' },
      ],
      dateStart: moment().tz('Asia/Bangkok').format('YYYY-MM-DD 00:00:00'),
      dateEnd: moment().tz('Asia/Bangkok').format('YYYY-MM-DD 23:59:59'),
      items: [],
      fields: [
        { key: 'index', label: 'No.' },
        { key: 'username', label: 'Username', sortable: true },
        { key: 'fullname', label: 'ชื่อ-นามสกุล' },
        { key: 'tel', label: 'เบอร์โทร' },
        { key: 'amount', label: 'จำนวนเงิน', sortable: true },
        { key: 'status', label: 'สถานะ', sortable: true },
        { key: 'dest_account', label: 'บัญชีปลายทาง' },
        { key: 'created_at', label: 'เวลา', sortable: true },
        { key: 'actions', label: 'แนบสลิป' },
      ],
      perPage: 10,
      pageOptions: [10, 25, 50, 100],
      totalRows: 0,
      currentPage: 1,
      sortBy: '',
      sortDesc: true,
      sortDirection: 'desc',
      selectedOrder: null,
      slipFile: null,
      slipPreview: null,
      uploading: false,
      eventSource: null,
      sseConnected: false,
    }
  },
  mounted() {
    this.getOrders()
    this.startSSE()
  },
  beforeDestroy() {
    this.stopSSE()
  },
  methods: {
    getSSEUrl() {
      const token = localStorage.getItem('token')
      const base = window.origin.includes('localhost')
        ? 'http://localhost:4040/api'
        : 'https://api.kimchi-backend.com/api'
      return `${base}/ezrich/orders/sse?token=${encodeURIComponent(token || '')}`
    },
    startSSE() {
      this.stopSSE()
      try {
        const url = this.getSSEUrl()
        this.eventSource = new EventSource(url)
        this.eventSource.onmessage = (event) => {
          try {
            const msg = JSON.parse(event.data)
            if (msg.type === 'connected') {
              this.sseConnected = true
              return
            }
            if (msg.type === 'order_updated') {
              this.getOrders(true)
              if (msg.changeType === 'insert') {
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: 'มีรายการฝาก Ezrich ใหม่',
                    text: `${msg.username} · ${Number(msg.amount).toLocaleString()} บาท`,
                    icon: 'BellIcon',
                    variant: 'info',
                  },
                })
              }
              if (msg.status === 'success') {
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: `ฝากสำเร็จ: ${msg.username}`,
                    text: `จำนวน ${Number(msg.amount).toLocaleString()} บาท`,
                    icon: 'CheckCircleIcon',
                    variant: 'success',
                  },
                })
              }
            }
          } catch (e) { /* ignore parse errors */ }
        }
        this.eventSource.onerror = () => {
          this.sseConnected = false
        }
      } catch (e) { /* ignore */ }
    },
    stopSSE() {
      if (this.eventSource) {
        this.eventSource.close()
        this.eventSource = null
        this.sseConnected = false
      }
    },
    getOrders(silent = false) {
      if (!silent) {
        this.show = true
      }
      const params = {
        search_type: this.search_type,
        search_val: this.search_val,
        dateStart: this.dateStart,
        dateEnd: this.dateEnd,
      }
      this.$http
        .get('/ezrich/orders', { params })
        .then(response => {
          this.show = false
          this.items = response.data
          this.totalRows = response.data.length
        })
        .catch(error => {
          this.show = false
          console.error(error)
        })
    },
    getDetail(item) {
      return item.data?.details?.[0] || null
    },
    formatDate(val) {
      return moment(val).tz('Asia/Bangkok').format('DD/MM/YYYY HH:mm:ss')
    },
    statusVariant(status) {
      const map = { waiting: 'warning', success: 'success', cancel: 'danger' }
      return map[status] || 'secondary'
    },
    statusText(status) {
      const map = { waiting: 'รอดำเนินการ', success: 'สำเร็จ', cancel: 'ยกเลิก' }
      return map[status] || status
    },
    selectOrder(item) {
      this.selectedOrder = item
    },
    onFileSelected(event) {
      const file = event.target.files[0]
      if (!file) return
      this.slipFile = file
      this.slipPreview = URL.createObjectURL(file)
    },
    clearSlip() {
      this.slipFile = null
      if (this.slipPreview) {
        URL.revokeObjectURL(this.slipPreview)
        this.slipPreview = null
      }
      if (this.$refs.slipInput) {
        this.$refs.slipInput.value = ''
      }
    },
    resetModal() {
      this.selectedOrder = null
      this.clearSlip()
      this.uploading = false
    },
    submitSlip(bvModalEvt) {
      bvModalEvt.preventDefault()
      if (!this.slipFile || !this.selectedOrder) return

      const detail = this.getDetail(this.selectedOrder)
      const transactionId = detail?.transaction_id
      if (!transactionId) {
        this.SwalError('ไม่พบ transaction_id ในรายการนี้')
        return
      }

      this.uploading = true
      const formData = new FormData()
      formData.append('slip_image', this.slipFile)
      formData.append('order_detail_id', transactionId)

      this.$http
        .post('/ezrich/upload-slip', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then(response => {
          this.uploading = false
          this.$bvModal.hide('modal-upload-slip')
          this.Success('อัพโหลดสลิปสำเร็จ รอระบบตรวจสอบ')
          this.getOrders()
        })
        .catch(error => {
          this.uploading = false
          const msg = error.response?.data?.message || 'อัพโหลดสลิปไม่สำเร็จ'
          this.SwalError(msg)
        })
    },
    Success(mes) {
      this.$swal({
        icon: 'success',
        title: '<h3 style="color: #141414">ทำรายการสำเร็จ</h3>',
        text: mes,
        customClass: {
          confirmButton: 'btn btn-success',
        },
      })
    },
    SwalError(mes) {
      this.$swal({
        icon: 'error',
        title: '<h3 style="color: #141414">เกิดข้อผิดพลาด!</h3>',
        text: mes,
        customClass: {
          confirmButton: 'btn btn-primary',
        },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~@core/scss/base/pages/app-invoice.scss";

.form-item-section {
  background-color: $product-details-bg;
}
</style>
<style lang="scss">
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>
